// ============================================================================
// Market P&L — the seat arithmetic (board item #1027, Ben 2026-11-02, option A).
//
// Ported from the approved demo's script (tcs-app-demo-market-pl-seat-check-
// 2026-11-02.html): STD, lineCost, seatCost, compute, scen, cls, lines, same.
// Same inputs give the same break-even — market-pl-math.test.ts holds that
// against the demo's own San Antonio figures. Do not reinvent this; change it
// only together with the demo it came from.
//
// Revenue and sub cost come live from v_market_pl_revenue; labor lines from
// v_market_pl_labor_lines; the standards from market_pl_assumptions. A line's
// base cost is the SAME formula v_market_pl_labor_lines uses (override, else
// the role standard × share) so an unsaved edit previews and a saved one ties;
// smoke-market-pl.mjs proves the two agree on every live line.
// ============================================================================

export type SeatKey = 'NM' | 'AM' | 'BDD';
export type LineRole = 'NM' | 'AM' | 'BDD' | 'SDR' | 'POOL';
export type Tone = 'g' | 'a' | 'r';

export interface Assumptions {
  nm_full_annual: number;
  nm_full_hours: number;
  am_annual: number;
  bdd_annual: number;
  sdr_annual: number;
  supplies_pct: number;
  w2_burden_pct: number;
  line_first_nm: number;
  line_first_am: number;
  line_second_nm: number;
  line_second_am: number;
}

/** The standards Ben set on 2 Nov 2026 — the column defaults; "Reset to standards". */
export const STANDARDS: Assumptions = {
  nm_full_annual: 48000,
  nm_full_hours: 30,
  am_annual: 85000,
  bdd_annual: 124000,
  sdr_annual: 75000,
  supplies_pct: 3.7,
  w2_burden_pct: 25,
  line_first_nm: 250000,
  line_first_am: 500000,
  line_second_nm: 2000000,
  line_second_am: 4000000,
};

export const ASSUMPTION_KEYS = Object.keys(STANDARDS) as (keyof Assumptions)[];

export function isEditedFromStandard(a: Assumptions): boolean {
  return ASSUMPTION_KEYS.some((k) => Number(a[k]) !== STANDARDS[k]);
}

export interface LaborLine {
  allocationId: string;
  employeeId: string | null;
  displayName: string;
  role: LineRole;
  share: number;
  override: number | null;
  note: string | null;
}

export interface MarketInput {
  marketName: string;
  dotColor: string;
  sitesActive: number;
  sitesSigned: number;
  /** Signed, not open, and left out of the money: no monthly rate or no sub cost recorded yet. */
  sitesSignedUncosted: number;
  revenueAnnual: number;
  subAnnual: number;
  signedRevenueAnnual: number;
  signedSubAnnual: number;
  avgMonthlyClinicAsc: number | null;
  avgMonthlyDialysis: number | null;
  lines: LaborLine[];
}

export interface Settings {
  a: Assumptions;
  seat: SeatKey;
  hours: number;
  burdenOn: boolean;
  signedOn: boolean;
  /**
   * Ben 2026-11-03 (option A): a new seat replaces the market's BORROWED lines of
   * the same role — a person whose biggest share is in another market — and that
   * share goes back to their home market. Off = the seat sits on top of everyone.
   */
  replaceBorrowed?: boolean;
  /** Each person's home market: the market of their largest live share (homeMarkets()). */
  homeOf?: ReadonlyMap<string, string>;
}

export interface MarketResult {
  input: MarketInput;
  sites: number;
  rev: number;
  sub: number;
  supplies: number;
  gp: number;
  gm: number;
  internal: number;
  contrib: number;
}

export interface Scenario {
  cost: number;
  after: number;
  short: number;
  be: number;
  gapMo: number;
  margin: number;
}

export const SEAT_LABEL: Record<SeatKey, string> = {
  NM: 'Night Manager',
  AM: 'Account Manager',
  BDD: 'BDD',
};

export function burdenFactor(s: Pick<Settings, 'a' | 'burdenOn'>): number {
  return s.burdenOn ? 1 + s.a.w2_burden_pct / 100 : 1;
}

function roleStandard(a: Assumptions, role: LineRole): number {
  switch (role) {
    case 'NM':
      return a.nm_full_annual;
    case 'AM':
      return a.am_annual;
    case 'BDD':
      return a.bdd_annual;
    case 'SDR':
      return a.sdr_annual;
    default:
      return 0; // POOL always carries an override (database CHECK).
  }
}

/** A line's base cost, before burden — the formula v_market_pl_labor_lines uses. */
export function lineBase(l: LaborLine, a: Assumptions): number {
  return l.override != null ? l.override : roleStandard(a, l.role) * l.share;
}

export function lineCost(l: LaborLine, s: Settings): number {
  return lineBase(l, s.a) * burdenFactor(s);
}

/** NM scales with planned hours against the full-time standard; AM/BDD are the standard. */
export function seatCost(k: SeatKey, s: Settings, hours = s.hours): number {
  const base =
    k === 'NM'
      ? (s.a.nm_full_annual * hours) / s.a.nm_full_hours
      : k === 'AM'
        ? s.a.am_annual
        : s.a.bdd_annual;
  return base * burdenFactor(s);
}

export function computeMarket(m: MarketInput, s: Settings): MarketResult {
  const rev = m.revenueAnnual + (s.signedOn ? m.signedRevenueAnnual : 0);
  const sub = m.subAnnual + (s.signedOn ? m.signedSubAnnual : 0);
  const supplies = (rev * s.a.supplies_pct) / 100;
  const gp = rev - sub - supplies;
  const gm = rev > 0 ? gp / rev : 0;
  const internal = m.lines.reduce((t, l) => t + lineCost(l, s), 0);
  return {
    input: m,
    sites: m.sitesActive + (s.signedOn ? m.sitesSigned : 0),
    rev,
    sub,
    supplies,
    gp,
    gm,
    internal,
    contrib: gp - internal,
  };
}

/** Gross margin % = (revenue − sub − supplies) ÷ revenue; break-even = rev + max(0, cost − contribution) ÷ gm. */
export function scenario(r: MarketResult, cost: number): Scenario {
  const after = r.contrib - cost;
  const short = Math.max(0, -after);
  const be = r.gm > 0 ? r.rev + short / r.gm : Number.POSITIVE_INFINITY;
  return {
    cost,
    after,
    short,
    be,
    gapMo: (be - r.rev) / 12,
    margin: r.rev > 0 ? after / r.rev : 0,
  };
}

/** A person's home market = the market holding their largest live share (first on a tie). */
export function homeMarkets(markets: MarketInput[]): Map<string, string> {
  const best = new Map<string, { market: string; share: number }>();
  for (const m of markets)
    for (const l of m.lines) {
      if (!l.employeeId) continue;
      const cur = best.get(l.employeeId);
      if (!cur || l.share > cur.share) best.set(l.employeeId, { market: m.marketName, share: l.share });
    }
  return new Map([...best].map(([eid, v]) => [eid, v.market]));
}

export interface ReplacedLine {
  line: LaborLine;
  /** The market the share goes back to. */
  home: string;
  /** What the market stops carrying (burden applied as for every line). */
  cost: number;
}

/** The lines a new seat of role k takes over in market m: borrowed lines of that role. */
export function replacedLines(m: MarketInput, k: SeatKey, s: Settings): ReplacedLine[] {
  if (!s.replaceBorrowed || !s.homeOf) return [];
  const out: ReplacedLine[] = [];
  for (const l of m.lines) {
    if (l.role !== k || !l.employeeId) continue;
    const home = s.homeOf.get(l.employeeId);
    if (!home || home === m.marketName) continue;
    out.push({ line: l, home, cost: lineCost(l, s) });
  }
  return out;
}

export interface SeatScenario extends Scenario {
  /** The seat's own cost before anything it replaces. */
  gross: number;
  replaced: ReplacedLine[];
}

/** The seat's cost is net of the borrowed shares it replaces (when the switch is on). */
export function seatScenario(r: MarketResult, k: SeatKey, s: Settings, hours = s.hours): SeatScenario {
  const gross = seatCost(k, s, hours);
  const replaced = replacedLines(r.input, k, s);
  const freed = replaced.reduce((t, x) => t + x.cost, 0);
  return { ...scenario(r, gross - freed), gross, replaced };
}

/** Green carries it; red if already negative or short $5,000+/mo; amber short under $5,000/mo. */
export function tone(r: MarketResult, sc: Scenario): Tone {
  if (sc.after >= 0) return 'g';
  if (r.contrib < 0) return 'r';
  return sc.gapMo < 5000 ? 'a' : 'r';
}

export interface LineTag {
  label: string;
  reached: boolean;
}

/** Bryan's hiring lines: 1st NM and 1st AM always shown; 2nd NM / 2nd AM only once reached. */
export function hiringLines(rev: number, a: Assumptions): LineTag[] {
  const t: [number, string][] = [
    [a.line_first_nm, 'NM'],
    [a.line_first_am, 'AM'],
    [a.line_second_nm, '2nd NM'],
    [a.line_second_am, '2nd AM'],
  ];
  return t
    .filter(([line], i) => i < 2 || rev >= line)
    .map(([line, label]) => ({ label, reached: rev >= line }));
}

/** Whole dollars, negatives in parentheses — the demo's money format. */
export function usd(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const a = Math.abs(Math.round(n)).toLocaleString('en-US');
  return n < 0 ? `($${a})` : `$${a}`;
}

export function pct(n: number, d = 1): string {
  if (!Number.isFinite(n)) return '—';
  const v = Math.abs(n * 100).toFixed(d);
  return n < 0 ? `(${v}%)` : `${v}%`;
}

/** "Same as N accounts" from the market's own clinic/ASC and dialysis averages. */
export function sameAs(m: MarketInput, gapMo: number): string {
  if (gapMo <= 0) return 'Covered today';
  if (!Number.isFinite(gapMo)) return '—';
  const p: string[] = [];
  if (m.avgMonthlyClinicAsc)
    p.push(`${(gapMo / m.avgMonthlyClinicAsc).toFixed(1)} clinic/ASC at ${usd(m.avgMonthlyClinicAsc)}`);
  if (m.avgMonthlyDialysis)
    p.push(`${(gapMo / m.avgMonthlyDialysis).toFixed(1)} dialysis at ${usd(m.avgMonthlyDialysis)}`);
  return p.join(', or ') || '—';
}

/** The verdict paragraph, in the demo's wording. */
export function verdictText(
  r: MarketResult,
  k: SeatKey,
  s: Settings,
): { tone: Tone; lead: string; body: string } {
  const name = r.input.marketName;
  const sc = seatScenario(r, k, s);
  const t = tone(r, sc);
  if (r.contrib < 0) {
    const g = r.gm > 0 ? -r.contrib / r.gm / 12 : Number.POSITIVE_INFINITY;
    return {
      tone: t,
      lead: `${name} is already ${usd(r.contrib)} a year with the seats it has.`,
      body: `It needs ${usd(g)} a month more revenue (${sameAs(r.input, g)}) before it pays for what it carries. Adding a ${SEAT_LABEL[k]} moves the break-even to ${usd(sc.be)}.`,
    };
  }
  if (sc.after >= 0) {
    return {
      tone: t,
      lead: `${name} carries a ${SEAT_LABEL[k]} today.`,
      body: `Contribution stays ${usd(sc.after)} (${pct(sc.margin)}) after the seat. No new revenue required.`,
    };
  }
  return {
    tone: t,
    lead: `${name} is ${usd(sc.gapMo)} a month short on a ${SEAT_LABEL[k]}.`,
    body: `Break-even revenue is ${usd(sc.be)}; the gap is about ${sameAs(r.input, sc.gapMo)}. Name the accounts and the date, or hold the seat.`,
  };
}

/** One person's live shares added up, for the over-allocated warning (a warning, never a block). */
export function overAllocated(
  lines: { employeeId: string | null; share: number; displayName: string }[],
): string[] {
  const by = new Map<string, { name: string; total: number }>();
  for (const l of lines) {
    if (!l.employeeId) continue;
    const cur = by.get(l.employeeId) ?? { name: l.displayName, total: 0 };
    cur.total += l.share;
    by.set(l.employeeId, cur);
  }
  return [...by.values()]
    .filter((v) => v.total > 1.000001)
    .map((v) => `${v.name} (${v.total.toFixed(4)})`);
}
